import { create } from 'zustand';

/*
 * LA FESTA DELLA GIORNATA: una CODA, non una card con due righe.
 *
 * Due numeri della home misurano la giornata — lo Streak (giorni consecutivi) e
 * l'obiettivo giornaliero (partite di oggi) — e fino a poco fa nessuno dei due
 * si faceva vedere nel momento in cui si muoveva: li si scopriva tornando sulla
 * home.
 *
 * I motivi possono essere DUE nello stesso istante: `dailyGoalTarget` vale 1 per
 * default, quindi la prima partita del giorno rinnova la striscia E completa
 * l'obiettivo. Il primo giro li impilava nello stesso velo; adesso vanno IN
 * SEQUENZA, prima lo streak e poi l'obiettivo.
 *
 * Non e' un dettaglio di presentazione: e' cio' che permette al velo di avere
 * una forma sola. Con un motivo per volta il contenuto e' sempre e soltanto
 * «emblema, titolo, numero» — e la versione senza card del telefono diventa
 * l'unica da disegnare, invece di un caso particolare del velo a due righe.
 */

/** Un motivo per festeggiare: il tipo decide emblema e parole, il numero e' cio' che si mostra. */
export type MotivoFesta = {
  tipo: 'streak' | 'obiettivo';
  /** Giorni di striscia, oppure il bersaglio raggiunto. */
  numero: number;
};

/**
 * Dove sta il motivo in scena.
 *
 * `uscita` non e' «chiuso»: e' il tempo dell'animazione d'uscita, durante il
 * quale il velo e' ancora montato. Lo chiude `congeda()`, che il componente
 * chiama a TIMER (vedi li' il perche' non su `animationend`).
 */
export type FaseFesta = 'dentro' | 'uscita';

/** Cio' che gli store che annunciano sanno dire: la firma NON e' cambiata dal
 *  primo giro, cosi' `multiplayer-store` e `puzzle-store` non si toccano. */
type MotiviFesta = {
  /** I giorni della striscia, quando si e' appena rinnovata. */
  giorni?: number;
  /** Il bersaglio raggiunto, quando le partite di oggi lo hanno centrato. */
  obiettivo?: number;
};

type FestaGiornataState = {
  /** I motivi ancora da mostrare: `coda[0]` e' quello in scena, vuota = niente. */
  coda: MotivoFesta[];
  fase: FaseFesta;
  mostra: (motivi: MotiviFesta) => void;
  chiudi: () => void;
  congeda: () => void;
};

/**
 * I motivi gia' festeggiati in questa sessione, uno per uno.
 *
 * E' una rete, non la regola: la regola vera e' del server per lo Streak (non
 * ri-emette, perche' al secondo giro il conteggio di oggi non e' piu' uno) e
 * dell'uguaglianza esatta per l'obiettivo. Questo copre il doppio evento e le
 * due schede.
 *
 * PER MOTIVO e non piu' per coppia: con la coda i due viaggiano separati, e una
 * firma unica `7/1` non saprebbe dire che lo streak e' gia' passato ma
 * l'obiettivo no. A livello di MODULO e non nello stato: un ricarico la perde,
 * ed e' innocuo — dopo un ricarico il server non annuncia piu' niente.
 */
const giaFesteggiati = new Set<string>();

const firma = (m: MotivoFesta): string => `${m.tipo}:${m.numero}`;

export const useFestaGiornataStore = create<FestaGiornataState>((set, get) => ({
  coda: [],
  fase: 'dentro',

  mostra: (motivi) => {
    // L'ordine e' quello di questo elenco: lo streak e' la testata della
    // giornata, l'obiettivo il suo seguito.
    const nuovi: MotivoFesta[] = [];
    if (motivi.giorni != null) nuovi.push({ tipo: 'streak', numero: motivi.giorni });
    if (motivi.obiettivo != null) nuovi.push({ tipo: 'obiettivo', numero: motivi.obiettivo });

    const daMostrare = nuovi.filter((m) => !giaFesteggiati.has(firma(m)));
    if (daMostrare.length === 0) return;
    for (const m of daMostrare) giaFesteggiati.add(firma(m));

    set({ coda: [...get().coda, ...daMostrare] });
  },

  /*
   * Non svuota: apre il tempo dell'uscita. Su una festa gia' in uscita e'
   * INERTE, e quella guardia serve: senza, il secondo tocco di chi tocca due
   * volte di fila si mangerebbe il motivo dopo prima che compaia.
   */
  chiudi: () => {
    if (get().coda.length === 0 || get().fase === 'uscita') return;
    set({ fase: 'uscita' });
  },

  /** Fine dell'uscita: via il motivo in scena, e se ne resta uno entra da solo. */
  congeda: () => set({ coda: get().coda.slice(1), fase: 'dentro' }),
}));

/** Scorciatoia per gli store che annunciano (multiplayer, problemi): leggerli
 *  con un hook non avrebbe senso, sono callback fuori da React. */
export function mostraFestaGiornata(motivi: MotiviFesta): void {
  useFestaGiornataStore.getState().mostra(motivi);
}

/** Test-only: azzera la memoria di cio' che e' gia' stato festeggiato. */
export function __resetFestaGiornata(): void {
  giaFesteggiati.clear();
  useFestaGiornataStore.setState({ coda: [], fase: 'dentro' });
}
